
import Button from '../../../components/base/Button';

export default function VPNSection() {
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="vpn" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center mb-4">
            <div className="w-2 h-2 bg-[#00D9FF] rounded-full mr-3"></div>
            <span className="text-[#00D9FF] font-semibold uppercase tracking-wider text-sm">VPN PROTECTION</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-[#0A1628] mb-6 leading-tight">
            Enterprise-Grade<br />
            <span className="text-[#00D9FF]">VPN Security</span>
          </h2>
          <p className="text-lg text-[#6B7B93] max-w-3xl mx-auto leading-relaxed">
            ELTROM VPN provides comprehensive digital protection with advanced encryption, 
            global server network, and lightning-fast connections for secure browsing.
          </p>
        </div>

        {/* Main VPN Features Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
          {/* Left - Image */}
          <div className="relative">
            <img
              src="https://public.readdy.ai/ai/img_res/5abd145108e548b4104de21a330c728a.jpg"
              alt="VPN Dashboard Interface"
              className="w-full h-80 object-cover rounded-2xl shadow-xl"
            />
            
            {/* Floating Stats Cards */}
            <div className="absolute -top-4 -right-4 bg-white p-4 rounded-xl shadow-lg border border-gray-100">
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-sm font-semibold text-[#0A1628]">Secure Connection</span>
              </div>
            </div>
            
            <div className="absolute -bottom-4 -left-4 bg-[#0A1628] text-white p-4 rounded-xl shadow-lg">
              <div className="text-center">
                <div className="text-2xl font-bold text-[#00D9FF]">256-bit</div>
                <div className="text-xs uppercase tracking-wide">AES Encryption</div>
              </div>
            </div>
          </div>

          {/* Right - Content */}
          <div>
            <h3 className="text-3xl font-bold text-[#0A1628] mb-6">
              Advanced VPN Technology for Maximum Protection
            </h3>
            <p className="text-[#6B7B93] mb-8 leading-relaxed">
              Our VPN solution combines cutting-edge encryption protocols with a robust global 
              infrastructure to ensure your data remains private and secure across all networks.
            </p>

            {/* Feature List */}
            <div className="space-y-4 mb-8">
              {[
                { icon: 'ri-shield-check-line', title: 'Military-Grade Encryption', desc: 'AES-256 encryption standard' },
                { icon: 'ri-global-line', title: 'Global Server Network', desc: 'Servers in 50+ countries' },
                { icon: 'ri-speed-up-line', title: 'Lightning Fast Speed', desc: 'Optimized for performance' },
                { icon: 'ri-eye-off-line', title: 'No-Log Policy', desc: 'Complete privacy protection' }
              ].map((feature, index) => (
                <div key={index} className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[#00D9FF]/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <i className={`${feature.icon} text-xl text-[#00D9FF]`}></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#0A1628] mb-1">{feature.title}</h4>
                    <p className="text-sm text-[#6B7B93]">{feature.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <Button 
              variant="primary" 
              size="lg"
              onClick={() => scrollToSection('contact')}
              className="group"
            >
              Start Protected Browsing
              <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform duration-200"></i>
            </Button>
          </div>
        </div>

        {/* How It Works */}
        <div className="bg-gradient-to-r from-[#F7F9FC] to-[#EDF2F7] rounded-3xl p-8 lg:p-12">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-[#0A1628] mb-4">How ELTROM VPN Works</h3>
            <p className="text-[#6B7B93] max-w-2xl mx-auto">
              Simple, secure, and seamless protection in three easy steps
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                step: '01',
                title: 'Connect',
                desc: 'Launch ELTROM VPN and connect to our secure network',
                icon: 'ri-play-circle-line'
              },
              {
                step: '02',
                title: 'Encrypt',
                desc: 'Your data is encrypted with military-grade AES-256 encryption',
                icon: 'ri-lock-line'
              },
              {
                step: '03',
                title: 'Browse Securely',
                desc: 'Enjoy complete privacy and unrestricted internet access',
                icon: 'ri-shield-check-line'
              }
            ].map((step, index) => (
              <div key={index} className="text-center">
                <div className="relative mb-6">
                  <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto shadow-lg border border-[#00D9FF]/20">
                    <i className={`${step.icon} text-3xl text-[#00D9FF]`}></i>
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-[#0A1628] text-white rounded-full flex items-center justify-center text-sm font-bold">
                    {step.step}
                  </div>
                </div>
                <h4 className="text-xl font-bold text-[#0A1628] mb-3">{step.title}</h4>
                <p className="text-[#6B7B93] leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
